/**
 * "Draft all sections" on a response document's Overview: the header action
 * (with its confirm dialog) and the inline notice that shows the run's
 * progress and how it ended. State lives in `useDraftAll`; the page renders
 * the two pieces in their own places.
 *
 * "Draft document" (R1) replaces it while the server's whole-document job is
 * switched on: the same header action, starting the job over every section
 * the user may have drafted. State lives in `useDraftDocument`; the job's
 * progress shows on its own card (PanelDocumentJob). Draft all sections stays
 * for the switch-off case until the old engine is removed.
 */

import { useState } from 'react'
import { AlertCircle, CheckCircle2, Loader2, Sparkles, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { UseDraftAllResult } from '@/hooks/useDraftAll'
import type { UseDraftDocumentResult } from '@/hooks/useDraftDocument'

function sections(count: number): string {
  return `${count} ${count === 1 ? 'section' : 'sections'}`
}

// ===================
// Header action + confirm dialog
// ===================

export function DraftAllButton({ draftAll }: { draftAll: UseDraftAllResult }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { run, isStarting, emptyCount, readySections, skipReasons } = draftAll
  const readyCount = readySections.length
  const isWorking = run !== null || isStarting

  const handleOpen = () => {
    setIsDialogOpen(true)
    // Counts can go stale while the page sits open; confirm against fresh ones.
    void draftAll.refresh()
  }

  return (
    <>
      <Button
        variant="ai-outline"
        size="sm"
        onClick={handleOpen}
        disabled={isWorking || emptyCount === 0}
      >
        {isWorking ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Sparkles className="h-4 w-4 mr-2" />
        )}
        {run ? 'Drafting…' : 'Draft all sections'}
      </Button>

      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {readyCount > 0
                ? `Draft ${readyCount} empty ${readyCount === 1 ? 'section' : 'sections'} with AI?`
                : 'No empty sections are ready to draft'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {readyCount > 0 && (
                  <p>Each section is planned, then drafted. Sections that already have text are left as they are.</p>
                )}
                {skipReasons.length > 0 && (
                  <ul className="list-disc pl-5">
                    {skipReasons.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{readyCount > 0 ? 'Cancel' : 'Close'}</AlertDialogCancel>
            {readyCount > 0 && (
              <AlertDialogAction onClick={() => void draftAll.start()}>
                Draft {sections(readyCount)}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// ===================
// Draft document (R1 whole-document job): header action + confirm dialog
// ===================

/** Placeholder copy until Kapil's hand test. */
function draftDocumentSummary(draftDocument: UseDraftDocumentResult): string[] {
  const { status, writableSections } = draftDocument
  const withText = writableSections.filter((section) => section.hasText).length
  if (status?.shape === 'document_row') {
    return [
      'The AI researches, plans and drafts the document in the background, so you can keep working.',
      ...(withText > 0 ? ["It already has text, so you'll get a suggestion to accept or reject, never a change."] : []),
    ]
  }
  return [
    `The AI researches, plans and drafts ${sections(writableSections.length)} in the background, so you can keep working.`,
    ...(withText > 0
      ? [`${withText} of them already ${withText === 1 ? 'has' : 'have'} text: those get a suggestion to accept or reject, never a change.`]
      : []),
  ]
}

export function DraftDocumentButton({ draftDocument }: { draftDocument: UseDraftDocumentResult }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { status, writableSections, skipReasons, isStarting, isRunning } = draftDocument
  const isWorking = isRunning || isStarting
  const canDraft = writableSections.length > 0

  const handleOpen = () => {
    setIsDialogOpen(true)
    // The status can go stale while the page sits open; confirm against a fresh one.
    void draftDocument.refresh()
  }

  return (
    <>
      {/* The one leading AI action on the page: black default + gradient sparkle (design rule, 2026-09-27). */}
      <Button
        size="sm"
        onClick={handleOpen}
        disabled={isWorking || (status?.sections.length ?? 0) === 0}
      >
        {isWorking ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <GradientSparkleIcon className="h-4 w-4 mr-2" />
        )}
        {isRunning ? 'Drafting…' : 'Draft document'}
      </Button>

      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {canDraft ? 'Draft this document with AI?' : "There's nothing here the AI can draft for you"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {canDraft && draftDocumentSummary(draftDocument).map((line) => <p key={line}>{line}</p>)}
                {skipReasons.length > 0 && (
                  <ul className="list-disc pl-5">
                    {skipReasons.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{canDraft ? 'Cancel' : 'Close'}</AlertDialogCancel>
            {canDraft && (
              <AlertDialogAction onClick={() => void draftDocument.start()}>
                Draft document
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// ===================
// Inline progress / result notice
// ===================

export function DraftAllNotice({ draftAll }: { draftAll: UseDraftAllResult }) {
  const { run, outcome, dismissOutcome } = draftAll

  if (run) {
    return (
      <div role="status" className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2 text-sm">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
        <span>Drafting empty sections with AI</span>
        {run.progressMessage && <span className="truncate text-meta">{run.progressMessage}</span>}
      </div>
    )
  }

  if (!outcome) return null

  let icon = <CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(var(--status-success))]" />
  let message: string
  let skipReasons: string[] = []
  if (outcome.kind === 'done') {
    message = `Drafted ${sections(outcome.drafted)}.`
    if (outcome.remaining > 0) {
      message += ` ${sections(outcome.remaining)} still empty — run Draft all sections again to finish.`
    }
    if (outcome.skipped > 0) {
      message += ` ${sections(outcome.skipped)} skipped.`
    }
    skipReasons = outcome.skipReasons
  } else if (outcome.kind === 'stopped') {
    icon = <AlertCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
    message = `Drafting was stopped. ${sections(outcome.drafted)} drafted before it stopped ${outcome.drafted === 1 ? 'is' : 'are'} saved.`
  } else {
    icon = <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
    message = `Drafting stopped: ${outcome.message}`
  }

  return (
    <div
      role={outcome.kind === 'failed' ? 'alert' : 'status'}
      className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2 text-sm"
    >
      {icon}
      <div className="flex-1">
        <span>{message}</span>
        {skipReasons.length > 0 && (
          <ul className="mt-1 list-disc pl-5">
            {skipReasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0"
        aria-label="Dismiss"
        onClick={dismissOutcome}
      >
        <X className="h-4 w-4 text-subtle" />
      </Button>
    </div>
  )
}
